// src/components/SimBieuDoMui.jsx
import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Grid, Box, Divider, useTheme, Chip, Stack } from '@mui/material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip as ReTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';
import classNames from 'classnames/bind';
import styles from './KinhDich.module.scss';
const cx = classNames.bind(styles);
const ENERGY_ORDER = ['Sinh Khí', 'Thiên Y', 'Diên Niên', 'Phục Vị', 'Ngũ Quỷ', 'Tuyệt Mệnh', 'Họa Hại', 'Lục Sát'];

const COLORS = {
  good: '#ef4444', // red
  bad: '#000000', // black
  bar: '#d630d9', // magenta-like for bad bars
  barGood: '#ef4444', // red for strong good bars
  neutral: '#8aa0c6',
  line: '#1e88e5',
};

function safeNumber(n) {
  if (n == null || Number.isNaN(Number(n))) return 0;
  return Number(n);
}

export default function SimBieuDoMui({ analysis }) {
  const theme = useTheme();
  const safeAnalysis = analysis || {};

  // prefer percentGoodSim/percentBadSim else percentGood/percentBad
  const percentGood = safeAnalysis.percentGoodSim ?? safeAnalysis.percentGood ?? 0;
  const percentBad = safeAnalysis.percentBadSim ?? safeAnalysis.percentBad ?? 0;

  // Build bar data
  const barData = useMemo(() => {
    // Nếu có duNienScores thì dùng luôn
    if (safeAnalysis.duNienScores) {
      return ENERGY_ORDER.map((e) => ({
        energy: e,
        value: safeNumber(safeAnalysis.duNienScores[e]),
      }));
    }

    // Fallback: nếu có chartData (cũ)
    if (Array.isArray(safeAnalysis.chartData) && safeAnalysis.chartData.length > 0) {
      const map = new Map();
      safeAnalysis.chartData.forEach((d) => map.set(d.energy, safeNumber(d.value)));
      return ENERGY_ORDER.map((e) => ({ energy: e, value: map.get(e) || 0 }));
    }

    // Fallback cuối: tính tần suất từ simEnergies/combinedEnergies
    const list =
      Array.isArray(safeAnalysis.combinedEnergies) && safeAnalysis.combinedEnergies.length
        ? safeAnalysis.combinedEnergies
        : Array.isArray(safeAnalysis.simEnergies)
        ? safeAnalysis.simEnergies
        : [];
    const freq = {};
    list.forEach((v) => {
      freq[v] = (freq[v] || 0) + 1;
    });
    return ENERGY_ORDER.map((e) => ({ energy: e, value: freq[e] || 0 }));
  }, [safeAnalysis]);
  // Pie data
  const pieData = [
    { name: 'Tốt', value: percentGood },
    { name: 'Xấu', value: percentBad },
  ];

  // Line chart
  const lineData = useMemo(() => {
    const pairs = safeAnalysis.phoneAnalysis?.pairs ?? safeAnalysis.simPairs ?? [];
    if (!Array.isArray(pairs) || pairs.length === 0) {
      const seq = safeAnalysis.simEnergies ?? [];
      return seq.map((s, i) => ({ idx: i + 1, label: s, y: 0 }));
    }

    // Map năng lượng -> biên độ sóng (cát dương, hung âm)
    const energyToWave = {
      'Sinh Khí': 100,
      'Thiên Y': 80,
      'Diên Niên': 60,
      'Phục Vị': 40,
      'Ngũ Quỷ': -100,
      'Tuyệt Mệnh': -120,
      'Họa Hại': -80,
      'Lục Sát': -60,
    };

    return pairs.map((p, i) => {
      const energy = p.energyName || 'Không xác định';
      const y = energyToWave[energy] ?? 0;
      const label = p.groupStr ?? p.pair ?? energy;
      return { idx: i + 1, label, y };
    });
  }, [safeAnalysis]);

  // Energies
  // const energiesInSim = useMemo(() => {
  //   const sim = Array.isArray(safeAnalysis.simEnergies) ? safeAnalysis.simEnergies : [];
  //   return Array.from(new Set(sim.filter(Boolean)));
  // }, [safeAnalysis]);

  // const missingGood = useMemo(() => {
  //   return ENERGY_ORDER.slice(0, 4).filter((g) => !energiesInSim.includes(g));
  // }, [energiesInSim]);

  // const sim = Array.isArray(safeAnalysis.simEnergies) ? safeAnalysis.simEnergies : [];
  // const energiesInSim = [...new Set(sim.filter(Boolean))];

  // Nếu không có analysis → hiển thị thông báo thay vì return null
  if (!analysis) {
    return (
      <Card sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="body2">Không có dữ liệu để hiển thị</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 4, boxShadow: 3 }}>
      <CardContent>
        <Stack direction={'row'} justifyContent={'center'} className={cx('font-title')}>
          BIỂU ĐỒ NĂNG LƯỢNG BÁT CỰC
        </Stack>
        <div className={cx('font-13')}>
          <h3>Khuyến cáo và cơ sở dữ liệu biểu đồ năng lượng</h3>

          <p className={cx('mb-5')}>
            Các biểu đồ dưới đây được xây dựng dựa trên hệ thống số hóa dữ liệu năng lượng theo phương pháp{' '}
            <strong>Bát Cực Linh Số</strong>. Trong đó, mỗi dạng năng lượng được chia thành <strong>4 cấp độ</strong>,
            tương ứng với
            <strong>4 mức chỉ số định lượng</strong> khác nhau.
          </p>

          <p className={cx('mb-5')}>
            Theo phương pháp luận này, <strong>chỉ số năng lượng cấp 1</strong> được lấy làm chuẩn mực với giá trị{' '}
            <strong>100</strong>, các cấp độ tiếp theo được quy đổi tương ứng giảm dần theo tỷ lệ. Cụ thể, đội ngũ
            chuyên gia tại
            <strong>Sim Kinh Dịch</strong> đã phân chia như sau (ví dụ với năng lượng
            <strong>Thiên Y</strong>):
          </p>

          <ul className={cx('mb-5')}>
            <li>
              <strong>Cấp 1 (13–31):</strong> Năng lượng quy đổi ~ <strong>100 điểm</strong>
            </li>
            <li>
              <strong>Cấp 2 (68–86):</strong> Năng lượng quy đổi ~ <strong>80 điểm</strong>
            </li>
            <li>
              <strong>Cấp 3 (49–94):</strong> Năng lượng quy đổi ~ <strong>60 điểm</strong>
            </li>
            <li>
              <strong>Cấp 4 (27–72):</strong> Năng lượng quy đổi ~ <strong>30 điểm</strong>
            </li>
          </ul>

          <blockquote className={cx('mb-5')}>
            <p>
              ⚠ <strong>Lưu ý quan trọng:</strong>
              <br />
              Các chỉ số nêu trên mang tính chất <strong>ước lượng</strong> dựa trên thực nghiệm và kinh nghiệm phân
              tích, <strong>không phải giá trị tuyệt đối</strong>. Tuy nhiên, việc lượng hóa này giúp xây dựng một
              <strong>hệ quy chiếu tương đối</strong>, hỗ trợ người dùng
              <strong>đánh giá mức năng lượng</strong> hiện tại của một số điện thoại, và{' '}
              <strong>so sánh với các yếu tố phong thủy khác</strong> như số căn cước công dân hoặc các mệnh lý tương
              ứng.
            </p>
            <p>
              <strong>
                Điều này đồng thời giúp cho ứng dụng xem bói năng lượng số trở nên minh bạch, rành mạch hơn cả con người
                trong việc luận giải sim phong thủy
              </strong>
              , từ đó{' '}
              <strong>tránh được tình trạng mơ hồ, cảm tính, hay bị dẫn dắt bởi những đánh giá thiếu cơ sở</strong>.
              Người dùng có thể tự mình đối chiếu và đưa ra quyết định một cách{' '}
              <strong>sáng suốt, có cơ sở luận lý rõ ràng</strong>.
            </p>
          </blockquote>
        </div>

        <Grid container spacing={2} alignItems="flex-start">
          {/* Pie Chart */}
          <Grid item xs={12} md={4}>
            <Box sx={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    innerRadius={34}
                    label={({ value }) => `${value}%`}
                    labelLine={false}
                  >
                    <Cell fill={COLORS.good} />
                    <Cell fill={COLORS.bad} />
                  </Pie>
                  <Legend
                    verticalAlign="top"
                    align="right"
                    payload={[
                      { id: 't', value: `Tốt (${percentGood}%)`, type: 'square', color: COLORS.good },
                      { id: 'x', value: `Xấu (${percentBad}%)`, type: 'square', color: COLORS.bad },
                    ]}
                  />
                  <ReTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          {/* Bar Chart */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Tổng năng lượng theo từng du niên
            </Typography>
            <Box sx={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="energy" tick={{ fontSize: 12 }} interval={0} dy={6} />
                  <YAxis />
                  <ReTooltip />
                  <Bar dataKey="value" name="Điểm">
                    {barData.map((entry, idx) => {
                      const isGood = ['Sinh Khí', 'Thiên Y', 'Diên Niên', 'Phục Vị'].includes(entry.energy);
                      const col = isGood ? COLORS.barGood : COLORS.bar;
                      return <Cell key={`cell-${idx}`} fill={col} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Line Chart */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1">Sóng năng lượng sim</Typography>
            <Box sx={{ width: '100%', height: 180, mt: 1 }}>
              <ResponsiveContainer>
                <LineChart data={lineData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} />
                  <YAxis />
                  <ReTooltip />
                  <Line type="monotone" dataKey="y" stroke={COLORS.line} strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          {/* Summary + Energies */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" color={'red'}>
              Kết luận nhanh
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              {(safeAnalysis.summaryLines || []).slice(0, 6).map((s, i) => (
                <li key={i}>
                  <Typography variant="body2">{s}</Typography>
                </li>
              ))}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle2" color={'blue'}>
              Năng lượng có trong SIM
            </Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', mt: 1 }}>
              {analysis?.energiesInSim.length ? (
                analysis?.energiesInSim.map((en) => (
                  <Chip
                    key={en}
                    label={en}
                    size="small"
                    color={['Sinh Khí', 'Thiên Y', 'Diên Niên', 'Phục Vị'].includes(en) ? 'error' : 'secondary'}
                    sx={{ mr: 0.5, mb: 0.5, mt: 1 }}
                  />
                ))
              ) : (
                <Typography variant="body2">Không có</Typography>
              )}
            </Stack>

            {/* <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Năng lượng tốt không có
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1 }}>
              {missingGood.length ? (
                missingGood.map((en) => (
                  <Chip key={en} label={en} color="warning" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                ))
              ) : (
                <Typography variant="body2">Không có</Typography>
              )}
            </Stack> */}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
